import { getFragmentData } from '@api/gql';
import { AppDrawer, AppDrawerHeader } from '@components';
import { Alert, CircularProgress, Stack } from '@mui/material';
import { EVENT_ID_PARAM } from '@SystemLog/organisms/SystemLogEvents';
import { syslogEventFragmentDocument } from '@SystemLog/services/gql';
import { useGetSyslogEvent } from '@SystemLog/services/systemLogQueries';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import SystemLogDetails from './SystemLogDetails';
import SystemLogDiff from './SystemLogDiff';

const DRAWER_WIDTH = 800;

export default function SystemLogDrawer() {
  const [queryParams, setQueryParams] = useSearchParams();
  const eventId = useMemo(() => queryParams.get(EVENT_ID_PARAM), [queryParams]);

  const handleClose = () => {
    queryParams.delete(EVENT_ID_PARAM);
    setQueryParams(queryParams);
  };

  return (
    <AppDrawer
      open={!!eventId}
      onClose={handleClose}
      header={<AppDrawerHeader title="Event details" onCloseClick={handleClose} />}
      width={DRAWER_WIDTH}
    >
      {eventId && <DrawerContent id={eventId} />}
    </AppDrawer>
  );
}

function DrawerContent({ id }: { id: string }) {
  const { data: eventData, isFetched } = useGetSyslogEvent(id);

  if (!isFetched) {
    return <CircularProgress />;
  }

  if (!eventData) {
    return <Alert severity="error">No event found</Alert>;
  }

  const event = {
    ...getFragmentData(syslogEventFragmentDocument, eventData),
    previousTargetObject: eventData.previousTargetObject,
    currentTargetObject: eventData.currentTargetObject,
  };

  return (
    <Stack spacing={2}>
      <SystemLogDetails event={event} />
      {(event.previousTargetObject || event.currentTargetObject) && (
        <SystemLogDiff previousObject={event.previousTargetObject} currentObject={event.currentTargetObject} />
      )}
    </Stack>
  );
}
